import React, { useState } from "react";
import { Heart, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";

export default function Donate() {
  const { addItem } = useCart();
  const [customAmount, setCustomAmount] = useState("");

  const addFixed = (amt) =>
    addItem({
      internal_id: `donation_${amt}`,
      name: `One-Time Donation $${amt}`,
      price: amt,
      type: "one_time",
    });

  const addCustom = () => {
    const amt = parseFloat(customAmount);
    if (!amt || amt < 5) {
      toast.error("Minimum custom donation is $5.00");
      return;
    }
    addItem({
      internal_id: "donation_custom",
      name: `Custom Donation $${amt.toFixed(2)}`,
      price: amt,
      custom_amount: amt,
      type: "one_time",
    });
    setCustomAmount("");
  };

  const addTier = (id, name, price) =>
    addItem({
      internal_id: id,
      name: `${name} (Monthly)`,
      price: price,
      type: "subscription",
    });

  return (
    <div className="min-h-screen" data-testid="donate-page">
      <section className="bg-royal-blue text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <Heart className="w-8 h-8 text-kingdom-gold" />
            <span className="text-kingdom-gold text-sm font-semibold uppercase tracking-wider">
              Support the Nation
            </span>
          </div>
          <h1 className="heading-hero mb-4">Donate &amp; Subscribe</h1>
          <p className="body-large text-slate-300 max-w-2xl">
            Your support helps build The Kingdom of Yunah'Shach'Qal Yashar'al. All checkout happens
            securely on this site.
          </p>
        </div>
      </section>

      <section className="bg-parchment py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="heading-section text-royal-blue mb-2">One-Time Donation</h2>
          <p className="text-slate-600 mb-8">Choose an amount &mdash; secure on-site checkout.</p>

          {/* Fixed donation buttons (unrolled to avoid babel-metadata-plugin recursion on .map) */}
          <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mb-6">
            <button type="button" className="btn-outline-royal py-4 rounded font-bold hover:bg-royal-blue hover:text-white transition-colors" onClick={() => addFixed(5)} data-testid="donate-onetime-5">$5</button>
            <button type="button" className="btn-outline-royal py-4 rounded font-bold hover:bg-royal-blue hover:text-white transition-colors" onClick={() => addFixed(10)} data-testid="donate-onetime-10">$10</button>
            <button type="button" className="btn-outline-royal py-4 rounded font-bold hover:bg-royal-blue hover:text-white transition-colors" onClick={() => addFixed(25)} data-testid="donate-onetime-25">$25</button>
            <button type="button" className="btn-outline-royal py-4 rounded font-bold hover:bg-royal-blue hover:text-white transition-colors" onClick={() => addFixed(50)} data-testid="donate-onetime-50">$50</button>
            <button type="button" className="btn-outline-royal py-4 rounded font-bold hover:bg-royal-blue hover:text-white transition-colors" onClick={() => addFixed(100)} data-testid="donate-onetime-100">$100</button>
            <button type="button" className="btn-outline-royal py-4 rounded font-bold hover:bg-royal-blue hover:text-white transition-colors" onClick={() => addFixed(250)} data-testid="donate-onetime-250">$250</button>
          </div>

          {/* Custom amount */}
          <div className="flex items-center justify-center gap-2 max-w-sm mx-auto mb-12">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
              <Input
                type="number"
                min="5"
                step="1"
                placeholder="Custom amount"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value)}
                className="pl-7"
                style={{ fontSize: "16px" }}
                data-testid="donate-custom-input"
              />
            </div>
            <Button className="btn-gold" onClick={addCustom} data-testid="donate-custom-add">
              Add to Cart
            </Button>
          </div>

          <h3 className="font-heading text-2xl text-royal-blue mb-2">Monthly Subscription Tiers</h3>
          <p className="text-slate-600 mb-8">True recurring monthly billing through on-site checkout. Cancel anytime.</p>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Supporter */}
            <div className="bg-white p-6 rounded-sm shadow border-t-4 border-slate-300 flex flex-col" data-testid="tier-card-supporter_monthly">
              <h4 className="font-heading text-xl text-royal-blue">Supporter</h4>
              <p className="text-3xl font-bold text-kingdom-gold my-2">$10<span className="text-sm text-slate-500">/mo</span></p>
              <ul className="text-sm text-slate-600 space-y-1 mb-4 text-left">
                <li>&bull; 1 free song download/month</li>
                <li>&bull; Early access to content</li>
              </ul>
              <button type="button" className="btn-gold block text-center py-2 rounded mt-auto" onClick={() => addTier("supporter_monthly", "Supporter", 10)} data-testid="tier-subscribe-supporter_monthly">Subscribe Monthly</button>
            </div>
            {/* Builder (popular) */}
            <div className="bg-white p-6 rounded-sm shadow border-t-4 border-kingdom-gold ring-2 ring-kingdom-gold flex flex-col" data-testid="tier-card-builder_monthly">
              <div className="bg-kingdom-gold text-white text-xs font-bold px-2 py-1 rounded inline-block mb-2 self-start">POPULAR</div>
              <h4 className="font-heading text-xl text-royal-blue">Builder</h4>
              <p className="text-3xl font-bold text-kingdom-gold my-2">$25<span className="text-sm text-slate-500">/mo</span></p>
              <ul className="text-sm text-slate-600 space-y-1 mb-4 text-left">
                <li>&bull; 1 free song download/month</li>
                <li>&bull; Exclusive content access</li>
                <li>&bull; Monthly video calls</li>
              </ul>
              <button type="button" className="btn-gold block text-center py-2 rounded mt-auto" onClick={() => addTier("builder_monthly", "Builder", 25)} data-testid="tier-subscribe-builder_monthly">Subscribe Monthly</button>
            </div>
            {/* Patron */}
            <div className="bg-white p-6 rounded-sm shadow border-t-4 border-royal-purple flex flex-col" data-testid="tier-card-patron_monthly">
              <h4 className="font-heading text-xl text-royal-blue">Patron</h4>
              <p className="text-3xl font-bold text-kingdom-gold my-2">$50<span className="text-sm text-slate-500">/mo</span></p>
              <ul className="text-sm text-slate-600 space-y-1 mb-4 text-left">
                <li>&bull; All Builder benefits</li>
                <li>&bull; Priority support</li>
                <li>&bull; One-time merch item</li>
              </ul>
              <button type="button" className="btn-gold block text-center py-2 rounded mt-auto" onClick={() => addTier("patron_monthly", "Patron", 50)} data-testid="tier-subscribe-patron_monthly">Subscribe Monthly</button>
            </div>
          </div>

          <p className="text-xs text-slate-500 mt-10">
            Recurring subscriptions are processed via secure on-site checkout. Card-on-file is authorized monthly until canceled.
          </p>
        </div>
      </section>

      {/* Nation Cash App (auxiliary tip — not the main checkout) */}
      <section className="bg-slate-100 py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-slate-500 text-sm mb-3">
            Prefer to send a quick external tip to the nation? You can also use Cash App.
          </p>
          <a
            href="https://cash.app/$Jonesaicayahsharal"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-slate-600 hover:text-royal-blue text-sm underline"
            data-testid="donate-cashapp-aux"
          >
            $Jonesaicayahsharal on Cash App
            <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </section>
    </div>
  );
}
